import React, { useState, useEffect } from "react";
import {
  Typography,
  Menu,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Button,
  useTheme,
} from "@mui/material";
import {
  createFolder,
  deleteItems,
  uploadFile,
  renameItem,
  downloadFile,
  copyItems,
  moveItems,
  zipItems,
  getSharedFoldersByPath,
} from "../api";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import FolderTree from "./FolderTree";
import { Box } from "@mui/material";
import { useUserContext } from "../Context/UserContext";
import FileUploadArea from "./FileUploadArea";

interface ActionMenuProps {
  parentFolderPath: string;
  selectedItems: Set<string>;
  clearSelectedItems: () => void;
  setSelectedItems: React.Dispatch<React.SetStateAction<Set<string>>>;
  rootFolder: string;
  renameTriggeredItem: string | null;
  onRenameComplete: () => void;
  onStatusChange: (message: string | null) => void;
  fetchFolders: (path: string) => Promise<void>;
}

const ActionMenu: React.FC<ActionMenuProps> = ({
  parentFolderPath,
  selectedItems,
  clearSelectedItems,
  setSelectedItems,
  rootFolder,
  renameTriggeredItem, // Adicionado
  onRenameComplete, // Adicionado
  onStatusChange,
  fetchFolders,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [openRenameDialog, setOpenRenameDialog] = useState(false); // Para renomear
  const [confirmReplaceDialog, setConfirmReplaceDialog] = useState(false);
  const [folderName, setFolderName] = useState("");
  const [newName, setNewName] = useState(""); // Para renomear
  const [fileList, setFileList] = useState<File[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const buttonStyle = {
    color: isDarkMode ? "#1976d2" : theme.palette.primary.main, // Azul escuro no tema escuro // Fundo transparente
    "&:hover": {},
    "&.Mui-disabled": {
      color: theme.palette.text.disabled, // Cor para estado desabilitado
      backgroundColor: "transparent", // Fundo transparente no estado desabilitado
    },
  };
  const [errorRename, setErrorRename] = useState<string | null>(null);
  const [openCopyDialog, setOpenCopyDialog] = useState(false);
  const [destinationPath, setDestinationPath] = useState("");
  const [_, setOpenFinalConfirmDialog] = useState(false); // Controle do segundo modal
  const [selectedItemPath, setSelectedItemPath] = useState("");
  const [openMoveDialog, setOpenMoveDialog] = useState(false); // Controle do modal
  const [moveDestinationPath, setMoveDestinationPath] = useState(""); // Caminho de destino
  const [openConfirmOverlay, setOpenConfirmOverlay] = useState(false);
  const [openMoveConfirmDialog, setOpenMoveConfirmDialog] = useState(false);
  const [openZipDialog, setOpenZipDialog] = useState(false);
  const [outputFileName, setOutputFileName] = useState("");
  const [openZipConfirmDialog, setOpenZipConfirmDialog] = useState(false);
  const invalidCharacters = /[\\/:*?"<>|.]/;
  const [copyFolders, setCopyFolders] = useState<Item[]>([]);
  const [moveFolders, setMoveFolders] = useState<Item[]>([]);

  const [relativeMovePath, setRelativeMovePath] = useState<string>("");
  const [relativeCopyPath, setRelativeCopyPath] = useState<string>("");
  const { breadcrumb, syncFolder, updatePathAndBreadcrumb, currentPath } =
    useUserContext();

  const isAtRootFolder = parentFolderPath === rootFolder;

  const user = JSON.parse(sessionStorage.getItem("user") || "{}");

  interface Item {
    path: string; // Caminho do arquivo ou pasta
    isDirectory: boolean; // Indica se é uma pasta
    accessPermission: boolean;
  }

  interface FolderNode {
    name: string;
    path: string;
    subfolders: FolderNode[];
  }

  const handleCopyDialogOpen = async () => {
    const user = JSON.parse(sessionStorage.getItem("user") || "{}");

    try {
      await syncFolder("", async () => {}); // Atualiza a árvore

      const rootFolders = await getSharedFoldersByPath({
        username: user.login,
        path: "",
      });

      const withPermission = rootFolders.filter(
        (f) => f.isDirectory && f.accessPermission
      );

      const filtered = withPermission.filter((folder) => {
        return !withPermission.some(
          (other) =>
            other.path !== folder.path &&
            other.path.startsWith(folder.path + "\\")
        );
      });

      setCopyFolders(
        filtered.map((f) => ({
          path: f.path,
          isDirectory: f.isDirectory,
          accessPermission: !!f.accessPermission,
        }))
      );

      setDestinationPath("");
      setOpenCopyDialog(true);
    } catch (error: any) {
      console.error("❌ Erro ao abrir diálogo de cópia:", error);
      toast.error(error.message || "Erro ao carregar pastas para copiar.");
    }
  };

  function getBreadcrumbPath(): string {
    if (!breadcrumb || breadcrumb.length === 0) {
      return "Área de Trabalho";
    }
    // Junta as labels com barra invertida
    const pathLabels = breadcrumb.map((b) => b.label).join("\\");
    return pathLabels;
  }

  const handleMoveDialogOpen = async () => {
    const user = JSON.parse(sessionStorage.getItem("user") || "{}");

    try {
      // 🔁 Garante que vai buscar a estrutura mais atualizada
      await syncFolder("", async () => {}); // ou algum callback vazio

      const rootFolders = await getSharedFoldersByPath({
        username: user.login,
        path: "",
      });

      const withPermission = rootFolders.filter(
        (f) => f.isDirectory && f.accessPermission
      );

      const filtered = withPermission.filter((folder) => {
        return !withPermission.some(
          (other) =>
            other.path !== folder.path &&
            other.path.startsWith(folder.path + "\\")
        );
      });

      const folderNodes: FolderNode[] = filtered.map((f) => ({
        name: f.path.split("\\").pop() || f.path,
        path: f.path,
        subfolders: [],
      }));

      setMoveFolders(
        filtered.map((f) => ({
          path: f.path,
          isDirectory: f.isDirectory,
          accessPermission: !!f.accessPermission,
        }))
      );

      setMoveDestinationPath("");
      setOpenMoveDialog(true);
    } catch (error: any) {
      toast.error(error.message || "Erro ao carregar pastas.");
    }
  };

  const handleZipDialogOpen = () => {
    setOpenZipDialog(true);
    setOutputFileName(""); // Reseta o nome do arquivo compactado
  };

  const handleZipDialogClose = () => {
    setOpenZipDialog(false);
    setOutputFileName(""); // Limpa o nome do arquivo de saída
  };

  const handleShowZipConfirmDialog = () => {
    if (!outputFileName.trim()) {
      toast.error("Insira um nome válido para o arquivo compactado.");
      return;
    }
    setOpenZipConfirmDialog(true); // Abre o diálogo de confirmação
  };

  const handleConfirmZipItems = async () => {
    const sourcePaths = Array.from(selectedItems);
    if (!sourcePaths.length || !outputFileName.trim()) {
      toast.error("Selecione itens e defina um nome válido.");
      return;
    }

    setIsLoading(true);

    try {
      const destination = parentFolderPath;
      const zipName = outputFileName.trim();

      await zipItems(user.login, destination, zipName, sourcePaths);

      await syncFolder(destination, fetchFolders);

      const updated = await getSharedFoldersByPath({
        username: user.login,
        path: "",
      });
      sessionStorage.setItem("userFolders", JSON.stringify(updated));

      toast.success(`Itens compactados com sucesso em "${zipName}.zip"!`);

      setOpenZipDialog(false);
      setOpenZipConfirmDialog(false);
      clearSelectedItems();
    } catch (error: any) {
      toast.error(error.message || "Erro ao compactar os itens.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyDialogClose = () => {
    setOpenCopyDialog(false);
    setDestinationPath(""); // Reseta o caminho de destino
  };

  const handleConfirmCopy = async () => {
    if (!selectedItems.size || !destinationPath.trim()) {
      toast.error("Selecione itens e um destino válido.");
      return;
    }

    const sourcePaths = Array.from(selectedItems);

    setIsLoading(true);

    try {
      await copyItems(user.login, sourcePaths, destinationPath.trim());

      await syncFolder(parentFolderPath, fetchFolders);

      const updated = await getSharedFoldersByPath({
        username: user.login,
        path: "",
      });

      sessionStorage.setItem("userFolders", JSON.stringify(updated));

      if (sourcePaths.length === 1) {
        toast.success(
          `Item ${selectedItemPath.split("\\").pop()} copiado com sucesso!`
        );
      } else {
        toast.success(`${sourcePaths.length} itens copiados com sucesso!`);
      }

      setOpenCopyDialog(false);
      setOpenFinalConfirmDialog(false);
      clearSelectedItems();
    } catch (error: any) {
      toast.error(error.message || "Erro ao copiar os itens.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const handleCopyDialogTrigger = async (event: Event) => {
      const customEvent = event as CustomEvent<string>;
      const sourcePath = customEvent.detail;
      if (sourcePath) {
        setSelectedItemPath(sourcePath);
        setSelectedItems(new Set([sourcePath]));
        await handleCopyDialogOpen();
      }
    };

    document.addEventListener("triggerCopyDialog", handleCopyDialogTrigger);

    return () => {
      document.removeEventListener(
        "triggerCopyDialog",
        handleCopyDialogTrigger
      );
    };
  }, []);

  useEffect(() => {
    const handleMoveDialogTrigger = async (event: Event) => {
      const customEvent = event as CustomEvent<string>;
      const sourcePath = customEvent.detail;
      if (sourcePath) {
        setSelectedItemPath(sourcePath);
        setSelectedItems(new Set([sourcePath])); // garante seleção
        await handleMoveDialogOpen(); // 🔥 aqui também
      }
    };

    document.addEventListener("triggerMoveDialog", handleMoveDialogTrigger);

    return () => {
      document.removeEventListener(
        "triggerMoveDialog",
        handleMoveDialogTrigger
      );
    };
  }, []);

  const handleShowConfirmOverlay = () => {
    if (!destinationPath.trim()) {
      toast.error("Selecione uma pasta de destino.");
      return;
    }

    // 1) Obtem o relativo, usando a mesma função:
    const relative = logRelativeDestination(destinationPath);

    // 2) Guarda no estado
    setRelativeCopyPath(relative);

    // 3) Continua o fluxo normal
    const sourcePath = Array.from(selectedItems)[0];
    setSelectedItemPath(sourcePath);
    setOpenConfirmOverlay(true);
  };

  const handleShowMoveConfirmOverlay = () => {
    if (!moveDestinationPath.trim()) {
      toast.error("Selecione uma pasta de destino.");
      return;
    }

    // 1) Obtemos o relativo chamando logRelativeDestination:
    const relative = logRelativeDestination(moveDestinationPath);

    // 2) Guardamos no estado:
    setRelativeMovePath(relative);

    // 3) Continua o fluxo normal
    setSelectedItemPath(Array.from(selectedItems)[0]);
    setOpenMoveConfirmDialog(true); // Abre o modal de confirmação
  };

  useEffect(() => {
    const handleCreateFolderTrigger = () => {
      handleDialogOpen(); // Abre o diálogo de criar pasta
    };

    document.addEventListener("triggerCreateFolder", handleCreateFolderTrigger);

    return () => {
      document.removeEventListener(
        "triggerCreateFolder",
        handleCreateFolderTrigger
      );
    };
  }, []);

  useEffect(() => {
    const handleDeleteTrigger = () => {
      if (selectedItems.size > 0) {
        setConfirmDelete(true); // Abre o diálogo de confirmação de exclusão
      }
    };

    document.addEventListener("triggerDelete", handleDeleteTrigger);

    return () => {
      document.removeEventListener("triggerDelete", handleDeleteTrigger);
    };
  }, [selectedItems]);

  useEffect(() => {
    const handleUploadTrigger = () => {
      handleUploadDialogOpen(); // Abre o diálogo de upload
    };

    document.addEventListener("triggerUpload", handleUploadTrigger);

    return () => {
      document.removeEventListener("triggerUpload", handleUploadTrigger);
    };
  }, []);

  useEffect(() => {
    if (renameTriggeredItem) {
      setOpenRenameDialog(true);
      const itemName = renameTriggeredItem.split("\\").pop() || "";
      const [nameBase] = itemName.split("."); // Extrai apenas o nome base, ignorando a extensão
      setNewName(nameBase);
    }
  }, [renameTriggeredItem]);

  const handleDialogOpen = () => {
    clearSelectedItems();
    setOpenDialog(true);
  };

  const handleSyncFromActionMenu = async (targetPath?: string) => {
    try {
      const user = JSON.parse(sessionStorage.getItem("user") || "{}");

      if (!user?.login) {
        toast.error("Usuário não autenticado.");
        return;
      }

      const sharedItems = await getSharedFoldersByPath({
        username: user.login,
        path: "",
      });

      const fixedItems = sharedItems.map((item) => ({
        ...item,
        path: item.path.replace(/\\\\/g, "\\"),
      }));

      sessionStorage.setItem("userFolders", JSON.stringify(fixedItems));

      const rootFolderPath = window._env_?.REACT_APP_ROOT_FOLDER || "";

      const topFolder = fixedItems
        .filter(
          (item) =>
            item.isDirectory &&
            item.accessPermission &&
            item.path !== rootFolderPath &&
            item.path.startsWith(rootFolderPath + "\\")
        )
        .sort((a, b) => a.path.length - b.path.length)[0];

      if (topFolder) {
        updatePathAndBreadcrumb(topFolder.path);
        await fetchFolders(topFolder.path);
      } else {
        updatePathAndBreadcrumb(""); // Área de Trabalho (sem path)
        await fetchFolders("");
      }
    } catch (error) {
      console.error("Erro ao sincronizar:", error);
      toast.error("Erro ao sincronizar os arquivos.");
    }
  };

  const handleMoveItems = async () => {
    if (selectedItems.size === 0) {
      toast.error("Selecione pelo menos um item para mover.");
      return;
    }

    setIsLoading(true);

    try {
      const sourcePaths = Array.from(selectedItems);
      const destination = moveDestinationPath.trim();

      await moveItems(user.login, sourcePaths, destination);

      await syncFolder(parentFolderPath, fetchFolders);

      const updated = await getSharedFoldersByPath({
        username: user.login,
        path: "",
      });
      sessionStorage.setItem("userFolders", JSON.stringify(updated));

      toast.success(
        sourcePaths.length === 1
          ? `Item ${selectedItemPath.split("\\").pop()} movido com sucesso!`
          : `${sourcePaths.length} itens movidos com sucesso!`
      );

      setOpenMoveDialog(false);
      clearSelectedItems();
    } catch (error: any) {
      toast.error(error.message || "Erro ao mover os itens.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setFolderName("");
    setError(null);
  };

  const handleRenameDialogOpen = () => {
    if (selectedItems.size === 1) {
      const currentPath = Array.from(selectedItems)[0]; // Pega o caminho completo do item selecionado
      const itemName = currentPath.split("\\").pop() || ""; // Extrai o nome completo do arquivo/pasta
      const nameWithoutExtension = itemName.includes(".")
        ? itemName.substring(0, itemName.lastIndexOf("."))
        : itemName; // Remove a extensão se for um arquivo

      setNewName(nameWithoutExtension); // Define o nome sem extensão no campo de texto
      setOpenRenameDialog(true); // Abre o modal
    } else {
      toast.error("Selecione apenas um item para renomear.");
    }
  };

  useEffect(() => {
    const handleDownloadTrigger = (event: Event) => {
      const customEvent = event as CustomEvent<string>;
      const filePath = customEvent.detail;
      if (filePath) {
        handleDownloadFile(filePath); // Chama o método de download
      }
    };

    document.addEventListener("triggerDownload", handleDownloadTrigger);

    return () => {
      document.removeEventListener("triggerDownload", handleDownloadTrigger);
    };
  }, []);

  const handleRenameDialogClose = () => {
    setOpenRenameDialog(false); // Fecha o modal
    setNewName(""); // Reseta o nome
    clearSelectedItems(); // Desmarca os itens selecionados
    if (onRenameComplete) {
      onRenameComplete(); // Notifica o componente pai
    }
  };

  const handleConfirmDeleteClose = () => {
    setConfirmDelete(false);
    clearSelectedItems(); // Desmarca os itens
  };

  const handleCreateFolder = async () => {
    if (!folderName.trim()) {
      toast.error("O nome da pasta não pode estar vazio.");
      return;
    }

    if (invalidCharacters.test(folderName)) {
      toast.error("O nome da pasta contém caracteres inválidos.");
      return;
    }

    setIsLoading(true);

    try {
      await createFolder(user.login, parentFolderPath, folderName);

      await syncFolder(parentFolderPath, fetchFolders);

      const updated = await getSharedFoldersByPath({
        username: user.login,
        path: "",
      });

      sessionStorage.setItem("userFolders", JSON.stringify(updated));

      window.dispatchEvent(
        new CustomEvent("refreshSidebarFolders", {
          detail: parentFolderPath,
        })
      );

      toast.success(`Pasta "${folderName}" criada com sucesso!`);
      setFolderName("");
      setOpenDialog(false);
      clearSelectedItems();
    } catch (error: any) {
      toast.error(error.message || "Erro ao criar a pasta.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRenameItem = async () => {
    if (selectedItems.size !== 1) {
      toast.error("Selecione apenas um item para renomear.");
      return;
    }

    const folderPaths = Array.from(selectedItems);

    const currentPathSelected = folderPaths[0];
    const itemName = currentPathSelected.split("\\").pop() || "";
    const isFile = itemName.includes(".");
    const originalExtension = isFile
      ? itemName.substring(itemName.lastIndexOf("."))
      : "";

    const updatedName = isFile
      ? `${newName.trim()}${originalExtension}`
      : newName.trim();

    if (!updatedName) {
      toast.error("O nome não pode estar vazio.");
      return;
    }

    setIsLoading(true);

    try {
      // 1. Renomeia via API
      await renameItem(user.login, currentPathSelected, updatedName);

      const parentPath = folderPaths[0].substring(
        0,
        folderPaths[0].lastIndexOf("\\")
      );
      const newPath = `${parentPath}\\${updatedName}`;
      const isRenamingCurrentFolder = currentPath === currentPathSelected;

      // 2. Atualiza a pasta atual ou o pai
      if (isRenamingCurrentFolder) {
        updatePathAndBreadcrumb(newPath);
        await syncFolder(newPath, fetchFolders);
      } else {
        await syncFolder(parentPath, fetchFolders);
      }

      // 3. Atualiza a árvore toda no sessionStorage
      const updated = await getSharedFoldersByPath({
        username: user.login,
        path: "",
      });
      sessionStorage.setItem("userFolders", JSON.stringify(updated));

      // 4. Atualiza breadcrumb se necessário
      if (isRenamingCurrentFolder) {
        await fetchFolders(newPath);
      }

      // 5. Dispara refresh na sidebar (nó pai)
      window.dispatchEvent(
        new CustomEvent("refreshSidebarFolders", {
          detail: parentPath,
        })
      );

      // 6. Feedback
      toast.success(`Item renomeado para "${updatedName}" com sucesso!`);
      handleRenameDialogClose();
      clearSelectedItems();
    } catch (error: any) {
      toast.error(error.message || "Erro ao renomear item.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteItems = async () => {
    if (selectedItems.size === 0) return;

    setIsLoading(true);
    try {
      const folderPaths = Array.from(selectedItems);
      await deleteItems(user.login, folderPaths);

      const anyDeletedIsCurrent = folderPaths.includes(currentPath);

      const parentPath = folderPaths[0].substring(
        0,
        folderPaths[0].lastIndexOf("\\")
      );

      // Atualiza visualização da pasta atual ou pai
      if (anyDeletedIsCurrent) {
        updatePathAndBreadcrumb(parentPath);
        await syncFolder(parentPath, fetchFolders);
      } else {
        await syncFolder(parentPath, fetchFolders);
      }

      // Atualiza o sessionStorage com a árvore completa
      const updated = await getSharedFoldersByPath({
        username: user.login,
        path: "",
      });
      sessionStorage.setItem("userFolders", JSON.stringify(updated));

      // Atualiza árvore lateral
      window.dispatchEvent(
        new CustomEvent("refreshSidebarFolders", { detail: parentPath })
      );

      toast.success("Itens excluídos com sucesso!");
    } catch (error: any) {
      toast.error(error.message || "Erro ao deletar itens.");
    } finally {
      setIsLoading(false);
      setConfirmDelete(false);
      clearSelectedItems();
    }
  };

  const handleUploadDialogOpen = () => {
    clearSelectedItems();
    setOpenUploadDialog(true);
  };

  const handleUploadDialogClose = () => {
    setOpenUploadDialog(false);
    setFileList([]);
  };

  const handleUploadFile = async () => {
    if (!fileList.length) return;

    setIsLoading(true);

    try {
      await uploadFile(user.login, parentFolderPath, fileList); // todos de uma vez

      await syncFolder(parentFolderPath, fetchFolders);

      const updated = await getSharedFoldersByPath({
        username: user.login,
        path: "",
      });

      sessionStorage.setItem("userFolders", JSON.stringify(updated));

      const folderPaths = Array.from(selectedItems);

      const currentPathSelected = folderPaths[0];

      const isRenamingCurrentFolder = currentPath === currentPathSelected;

      if (isRenamingCurrentFolder) {
        await fetchFolders(parentFolderPath);
      } else {
        updatePathAndBreadcrumb(parentFolderPath);
      }

      window.dispatchEvent(
        new CustomEvent("refreshSidebarFolders", {
          detail: parentFolderPath,
        })
      );

      toast.success(
        fileList.length === 1
          ? `Arquivo "${fileList[0].name}" enviado com sucesso!`
          : `${fileList.length} arquivos enviados com sucesso!`
      );

      setOpenUploadDialog(false);
      setFileList([]);
      clearSelectedItems();
    } catch (error: any) {
      toast.error(error.message || "Erro ao enviar arquivos.");
    } finally {
      setIsLoading(false);
    }
  };


  function getAllUserFolders(): Item[] {
    const stored = sessionStorage.getItem("userFolders") || "[]";
    const parsed = JSON.parse(stored);
    return parsed;
  }

  function getHighestDirectory(): string | null {
    const all = getAllUserFolders();
    // Filtra somente diretórios com permissão
    const dirs = all.filter(
      (item) => item.isDirectory && item.accessPermission
    );
    if (!dirs.length) return null;

    // Ordena do "menos profundo" para o "mais profundo"
    // Critério: contar quantas barras invertidas há no path
    dirs.sort((a, b) => a.path.split("\\").length - b.path.split("\\").length);

    // O primeiro item desse sort deve ser o "mais alto"
    return dirs[0].path;
  }

  function getPrefix(basePath: string): string {
    // Quebra por barra invertida
    const segments = basePath.split("\\");
    // Remove o último segmento (no exemplo, "CE101")
    segments.pop();
    // Junta de volta com "\"
    return segments.join("\\") + "\\";
  }

  function removePrefix(fullPath: string, prefix: string): string {
    return fullPath.replace(prefix, "");
  }

  function logRelativeDestination(moveDestinationPath: string): string {
    const highest = getHighestDirectory();
    if (!highest) {
      // Retorna algo em branco ou um texto padrão
      return "";
    }

    const prefix = getPrefix(highest);
    const relative = removePrefix(moveDestinationPath, prefix);
    return relative;
  }

  const handleConfirmReplace = async () => {
    setConfirmReplaceDialog(false);
    setIsLoading(true);

    try {
      for (const f of fileList) {
        await uploadFile(user.login, parentFolderPath, [f]);
      }

      const updated = await getSharedFoldersByPath({
        username: user.login,
        path: parentFolderPath,
      });

      sessionStorage.setItem("userFolders", JSON.stringify(updated));
      await fetchFolders(parentFolderPath);

      toast.success(
        fileList.length === 1
          ? `Arquivo "${fileList[0].name}" substituído com sucesso!`
          : `${fileList.length} arquivos substituídos com sucesso!`
      );

      setOpenUploadDialog(false);
      setFileList([]);
    } catch (error: any) {
      toast.error(error.message || "Erro ao substituir os arquivos.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadFile = async (filePath: string) => {
    const username = sessionStorage.getItem("username");
    if (!username) {
      toast.error("Usuário não autenticado.");
      return;
    }

    try {
      const blob = await downloadFile(username, filePath); // Chama o método do API

      // Criar o link para baixar o arquivo
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download =
        filePath.replace(/\\/g, "/").split("/").pop() || "download";

      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.info("Download iniciado. salve o arquivo.");
    } catch (error: any) {
      toast.error(error.message || "Erro ao realizar o download.");
    } finally {
      clearSelectedItems(); // Desmarca os itens selecionados
    }
  };

  const isFileSelected = (): boolean => {
    if (selectedItems.size !== 1) return false;

    const selectedItem = Array.from(selectedItems)[0];

    return selectedItem.includes(".");
  };

  useEffect(() => {
    if (invalidCharacters.test(folderName)) {
      setError(
        'O nome da pasta contém caracteres inválidos: \\ / : * ? " < > | .'
      );
    } else {
      setError(null);
    }
  }, [folderName]);

  useEffect(() => {
    if (invalidCharacters.test(newName)) {
      setErrorRename(
        'O novo nome contém caracteres inválidos: \\ / : * ? " < > | .'
      );
    } else {
      setErrorRename(null);
    }
  }, [newName]);

  useEffect(() => {
    const handleZipDialogTrigger = (event: Event) => {
      const customEvent = event as CustomEvent<{
        itemPath: string;
        itemName: string;
      }>;
      const { itemPath, itemName } = customEvent.detail;

      if (itemPath && itemName) {
        setSelectedItems(new Set([itemPath])); // Seleciona o item
        setOutputFileName(itemName.replace(/\.[^/.]+$/, "")); // Remove a extensão para sugerir o nome do arquivo compactado
        setOpenZipDialog(true); // Abre o diálogo de compactação
      }
    };

    document.addEventListener("triggerZipDialog", handleZipDialogTrigger);

    return () => {
      document.removeEventListener("triggerZipDialog", handleZipDialogTrigger);
    };
  }, []);

  useEffect(() => {
    if (currentPath) {
      fetchFolders(currentPath);
    }
  }, [currentPath]);

  return (
    <div style={{ display: "inline-block" }}>
      <Typography
        onMouseEnter={() => setIsMenuOpen(true)} // Abre o menu ao passar o mouse
        onMouseLeave={() => setIsMenuOpen(false)} // Fecha o menu ao sair
        sx={{
          color: theme.palette.text.primary,
          cursor: "pointer",
          "&:hover": { textDecoration: "underline" },
        }}
      >
        Ações
      </Typography>

      {isMenuOpen && (
        <Box
          onMouseEnter={() => setIsMenuOpen(true)} // Mantém o menu aberto
          onMouseLeave={() => {
            setIsMenuOpen(false);
            onStatusChange(null); // Limpa a mensagem ao fechar o menu
          }}
          sx={{
            position: "absolute",
            backgroundColor: isDarkMode ? "#424242" : "#d4eaff",
            color: theme.palette.text.primary,
            boxShadow: "0px 6px 10px rgba(0,0,0,0.5)",
            borderRadius: "4px",
            zIndex: 10,
            minWidth: "150px",
            padding: "8px 0",
          }}
        >
          {/* Baixar (Download) */}
          <Box
            onMouseEnter={() => {
              if (isAtRootFolder) {
                onStatusChange("Ação não permitida na Área de Trabalho.");
              } else if (!isFileSelected()) {
                onStatusChange(
                  "Selecione um único arquivo para efetuar o Download."
                );
              }
            }}
            onMouseLeave={() => onStatusChange(null)}
          >
            <MenuItem
              onClick={() => {
                const filePath = Array.from(selectedItems)[0]; // Obtenha o caminho do arquivo selecionado
                handleDownloadFile(filePath); // Passe o caminho como argumento
                setIsMenuOpen(false);
              }}
              disabled={!isFileSelected() || isAtRootFolder} // 🚫 Bloqueia na Área de Trabalho
            >
              <Typography>Baixar (Download)</Typography>
            </MenuItem>
          </Box>

          {/* Enviar (Upload) */}
          <Box
            onMouseEnter={() => {
              if (parentFolderPath === rootFolder) {
                onStatusChange("Ação não permitida na Área de Trabalho.");
              } else if (selectedItems.size > 0) {
                onStatusChange(
                  "Nenhum item pode estar selecionado para efetuar Upload."
                );
              }
            }}
            onMouseLeave={() => onStatusChange(null)}
          >
            <MenuItem
              onClick={() => {
                handleUploadDialogOpen();
                setIsMenuOpen(false);
              }}
              disabled={
                selectedItems.size > 0 || parentFolderPath === rootFolder
              } // Desativa na rootFolder
            >
              <Typography>Enviar (Upload)</Typography>
            </MenuItem>
          </Box>

          {/* Renomear Item */}
          <Box
            onMouseEnter={() => {
              if (isAtRootFolder) {
                onStatusChange("Ação não permitida na Área de Trabalho.");
              } else if (selectedItems.size !== 1) {
                onStatusChange("Selecione exatamente um item para renomear.");
              }
            }}
            onMouseLeave={() => onStatusChange(null)}
          >
            <MenuItem
              onClick={() => {
                handleRenameDialogOpen();
                setIsMenuOpen(false);
              }}
              disabled={selectedItems.size !== 1 || isAtRootFolder}
            >
              <Typography>Renomear Item</Typography>
            </MenuItem>
          </Box>

          {/* Copiar Itens */}
          {/* <Box
            onMouseEnter={() => {
              if (isAtRootFolder) {
                onStatusChange("Ação não permitida na Área de Trabalho.");
              } else if (selectedItems.size === 0) {
                onStatusChange("Selecione pelo menos um item para copiar.");
              }
            }}
            onMouseLeave={() => onStatusChange(null)}
          >
            <MenuItem
              onClick={() => {
                handleCopyDialogOpen();
                setIsMenuOpen(false);
              }}
              disabled={selectedItems.size === 0 || isAtRootFolder}
            >
              <Typography>Copiar Itens</Typography>
            </MenuItem>
          </Box> */}

          {/* Mover Itens */}
          {/* Mover Itens */}
          <Box
            onMouseEnter={() => {
              if (isAtRootFolder) {
                onStatusChange("Ação não permitida na Área de Trabalho.");
              } else if (selectedItems.size === 0) {
                onStatusChange("Selecione pelo menos um item para mover.");
              }
            }}
            onMouseLeave={() => onStatusChange(null)}
          >
            <MenuItem
              onClick={() => {
                handleMoveDialogOpen();
                setIsMenuOpen(false);
              }}
              disabled={selectedItems.size === 0 || isAtRootFolder}
            >
              <Typography>Mover Itens</Typography>
            </MenuItem>
          </Box>

          {/* Excluir Itens */}
          <Box
            onMouseEnter={() => {
              if (isAtRootFolder) {
                onStatusChange("Ação não permitida na Área de Trabalho.");
              } else if (selectedItems.size === 0) {
                onStatusChange("Selecione pelo menos um item para excluir.");
              }
            }}
            onMouseLeave={() => onStatusChange(null)}
          >
            <MenuItem
              onClick={() => {
                setConfirmDelete(true);
                setIsMenuOpen(false);
              }}
              disabled={selectedItems.size === 0 || isAtRootFolder}
            >
              <Typography>Excluir Itens</Typography>
            </MenuItem>
          </Box>

          {/* Compactar Itens */}
          <Box
            onMouseEnter={() => {
              if (isAtRootFolder) {
                onStatusChange("Ação não permitida na Área de Trabalho.");
              } else if (selectedItems.size === 0) {
                onStatusChange("Selecione pelo menos um item para compactar.");
              }
            }}
            onMouseLeave={() => onStatusChange(null)}
          >
            <MenuItem
              onClick={() => {
                handleZipDialogOpen();
                setIsMenuOpen(false);
              }}
              disabled={selectedItems.size === 0 || isAtRootFolder}
            >
              <Typography>Compactar Itens</Typography>
            </MenuItem>
          </Box>

          {/* Criar Pasta/Diretório */}
          <Box
            onMouseEnter={() => {
              if (parentFolderPath === rootFolder) {
                onStatusChange("Ação não permitida na Área de Trabalho.");
              } else if (selectedItems.size > 0) {
                onStatusChange(
                  "Nenhum item pode estar selecionado para criar uma nova pasta."
                );
              }
            }}
            onMouseLeave={() => onStatusChange(null)}
          >
            <MenuItem
              onClick={() => {
                handleDialogOpen();
                setIsMenuOpen(false);
              }}
              disabled={
                selectedItems.size > 0 || parentFolderPath === rootFolder
              }
            >
              <Typography>Criar Pasta/Diretório</Typography>
            </MenuItem>
          </Box>
        </Box>
      )}
      <Dialog
        open={openDialog}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            backgroundColor: isDarkMode ? "#424242" : "#fff",
            color: theme.palette.text.primary,
          }}
        >
          Nova Pasta
        </DialogTitle>
        <DialogContent
          sx={{
            backgroundColor: isDarkMode ? "#424242" : "#fff",
            minWidth: 400,
          }}
        >
          <DialogContentText
            sx={{ color: theme.palette.text.primary, marginBottom: 2 }}
          >
            Criar pasta na localização atual:
          </DialogContentText>
          <strong>
            {breadcrumb.length > 0
              ? getBreadcrumbPath()
              : parentFolderPath.replace(rootFolder, "Área de Trabalho")}
          </strong>
          <TextField
            autoFocus
            margin="dense"
            label="Nome da Pasta"
            type="text"
            fullWidth
            variant="outlined"
            value={folderName}
            onChange={(e) => setFolderName(e.target.value)}
            error={!!error}
            helperText={error}
            InputProps={{
              sx: {
                color: theme.palette.text.primary,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: isDarkMode
                    ? "#90caf9"
                    : theme.palette.primary.main,
                },
              },
            }}
            InputLabelProps={{
              sx: {
                color: isDarkMode ? "#90caf9" : theme.palette.primary.main,
              },
            }}
          />
        </DialogContent>
        <DialogActions
          sx={{ backgroundColor: isDarkMode ? "#424242" : "#fff" }}
        >
          <Button
            onClick={handleDialogClose}
            sx={{ color: isDarkMode ? "#90caf9" : theme.palette.primary.main }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleCreateFolder}
            disabled={!folderName.trim() || isLoading || !!error}
            sx={buttonStyle}
          >
            {isLoading ? "Criando..." : "Criar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmReplaceDialog}
        onClose={() => setConfirmReplaceDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Aviso</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Arquivo com este nome já existe na pasta e será substituído. Deseja
            continuar?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmReplaceDialog(false)}>
            Cancelar
          </Button>
          <Button onClick={handleConfirmReplace}>Continuar</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmDelete}
        onClose={handleConfirmDeleteClose} // Garante que itens sejam desmarcados
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Excluir Itens</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Deseja excluir os itens selecionados?
          </DialogContentText>
          <strong>(Pastas com seus conteúdos serão excluídas.)</strong>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmDeleteClose}>Cancelar</Button>
          <Button
            onClick={handleDeleteItems}
            disabled={isLoading}
            sx={buttonStyle}
          >
            {isLoading ? "Excluindo..." : "Excluir"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openCopyDialog}
        onClose={handleCopyDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            zIndex: openConfirmOverlay ? 1200 : 1300, // Ajusta a ordem de exibição
          },
        }}
      >
        <DialogTitle>Copiar Item</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Selecione a pasta de destino para copiar o item selecionado:
          </DialogContentText>
          <FolderTree
            folders={copyFolders}
            onFolderSelect={(folderPath) => setDestinationPath(folderPath)}
            selectedFolder={destinationPath}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCopyDialogClose}>Cancelar</Button>
          <Button
            onClick={handleShowConfirmOverlay}
            disabled={!destinationPath.trim()}
            sx={buttonStyle}
          >
            Confirmar Destino
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openMoveConfirmDialog}
        onClose={() => setOpenMoveConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Confirmação de Movimentação
          {/* Debug opcional: mostra o path real no título */}
          {/* <br />
    <small style={{ fontSize: "12px", fontWeight: "normal" }}>
      (Destino: {moveDestinationPath})
    </small> */}
        </DialogTitle>

        <DialogContent>
          <DialogContentText>
            Confirmar a movimentação dos seguintes itens:
            <ul>
              {Array.from(selectedItems).map((item) => (
                <li key={item}>
                  <strong>{item.split("\\").pop()}</strong>
                </li>
              ))}
            </ul>
            <br />
            Para:
            <strong>
              {relativeMovePath
                ? `Área de trabalho\\${relativeMovePath}`
                : "Caminho não encontrado"}
            </strong>
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenMoveConfirmDialog(false)}>
            Cancelar
          </Button>
          <Button
            onClick={async () => {
              await handleMoveItems(); // Executa a movimentação
              setOpenMoveConfirmDialog(false); // Fecha o modal
            }}
            disabled={isLoading}
            sx={buttonStyle}
          >
            {isLoading ? "Movendo..." : "Confirmar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openMoveDialog}
        onClose={() => setOpenMoveDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Mover Item</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Selecione a pasta de destino para mover o item selecionado:
          </DialogContentText>
          <FolderTree
            folders={moveFolders}
            onFolderSelect={(folderPath) => setMoveDestinationPath(folderPath)}
            selectedFolder={moveDestinationPath}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenMoveDialog(false)}>Cancelar</Button>
          <Button
            onClick={() => {
              if (moveDestinationPath.trim()) {
                // ✅ Dispara evento para expandir a pasta destino na árvore lateral
                window.dispatchEvent(
                  new CustomEvent("refreshSidebarFolders", {
                    detail: moveDestinationPath.trim(),
                  })
                );

                handleShowMoveConfirmOverlay(); // Abre o modal de confirmação
              } else {
                toast.error("Selecione uma pasta de destino.");
              }
            }}
            disabled={!moveDestinationPath.trim()}
            sx={buttonStyle}
          >
            Confirmar Destino
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openConfirmOverlay}
        onClose={() => setOpenConfirmOverlay(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirmação</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Confirmar a cópia dos itens selecionados:
            <ul>
              {Array.from(selectedItems).map((item) => (
                <li key={item}>
                  <strong>{item.split("\\").pop()}</strong>
                </li>
              ))}
            </ul>
            <br />
            Para:
            <strong>
              {/* exibe o caminho relativo ao invés de `destinationPath.replace(...)` */}
              {relativeCopyPath
                ? `Área de trabalho\\${relativeCopyPath}`
                : "Caminho não encontrado"}
            </strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmOverlay(false)}>Cancelar</Button>
          <Button
            onClick={async () => {
              await handleConfirmCopy();
              setOpenConfirmOverlay(false);
              setOpenCopyDialog(false); // Fecha o diálogo de cópia após a confirmação
            }}
            disabled={isLoading}
          >
            {isLoading ? "Copiando..." : "Confirmar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openZipDialog}
        onClose={handleZipDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Compactar Itens</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Insira o nome do arquivo compactado e confirme:
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Nome do Arquivo Compactado"
            type="text"
            fullWidth
            variant="outlined"
            value={outputFileName}
            onChange={(e) => setOutputFileName(e.target.value)}
            error={invalidCharacters.test(outputFileName)}
            helperText={
              invalidCharacters.test(outputFileName)
                ? 'O nome contém caracteres inválidos: \\ / : * ? " < > | .'
                : ""
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleZipDialogClose}>Cancelar</Button>
          <Button
            onClick={handleShowZipConfirmDialog} // Abre o modal de confirmação
            disabled={
              !outputFileName.trim() ||
              invalidCharacters.test(outputFileName) ||
              isLoading
            }
            sx={buttonStyle}
          >
            {isLoading ? "Verificando..." : "Compactar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openZipConfirmDialog}
        onClose={() => setOpenZipConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirmação de Compactação</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Confirmar a compactação dos seguintes itens:
            <ul>
              {Array.from(selectedItems).map((item) => (
                <li key={item}>
                  <strong>{item.split("\\").pop()}</strong>
                </li>
              ))}
            </ul>
            Para o arquivo: <strong> {`${outputFileName.trim()}.zip`} </strong>
            <br />
            <br />
            Na localização atual:
            <br />
            <strong>
              {breadcrumb.length > 0
                ? getBreadcrumbPath()
                : parentFolderPath.replace(rootFolder, "Área de Trabalho")}
            </strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenZipConfirmDialog(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmZipItems}
            disabled={isLoading}
            sx={buttonStyle}
          >
            {isLoading ? "Compactando..." : "Confirmar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openUploadDialog}
        onClose={handleUploadDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Enviar Arquivo</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Upload de arquivo para a localização atual:
          </DialogContentText>
          <strong>
            {breadcrumb.length > 0
              ? getBreadcrumbPath()
              : parentFolderPath.replace(rootFolder, "Área de Trabalho")}
          </strong>
          <br />
          <br />
          <input
            type="file"
            multiple
            onChange={(e) => {
              if (e.target.files && e.target.files.length > 0) {
                setFileList(Array.from(e.target.files)); // novo estado
              }
            }}
          />

          <FileUploadArea
            currentPath={parentFolderPath}
            onConfirmUpload={(files) => setFileList(files)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUploadDialogClose}>Cancelar</Button>
          <Button
            onClick={handleUploadFile}
            disabled={!fileList.length || isLoading}
            sx={buttonStyle}
          >
            {isLoading ? "Enviando..." : "Enviar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openRenameDialog}
        onClose={handleRenameDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Renomear Item</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Insira o novo nome para o item selecionado:
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Novo Nome"
            type="text"
            fullWidth
            variant="outlined"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            error={!!errorRename}
            helperText={errorRename}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRenameDialogClose}>Cancelar</Button>{" "}
          {/* Já chama o método que desmarca os itens */}
          <Button
            onClick={handleRenameItem}
            disabled={!newName.trim() || isLoading || !!errorRename}
            sx={buttonStyle}
          >
            Renomear
          </Button>
        </DialogActions>
      </Dialog>
      <ToastContainer />
    </div>
  );
};

export default ActionMenu;
